/**
 * Gates a devolved-service route at the point the URL is actually opened
 * (PH-CLIN1-13, pharmacy audit 2026-09-15).
 *
 * The sidebar and hub already hide a devolved module's link from a branch in
 * the wrong nation, via `moduleVisibleInNation` — but that only decides what
 * is offered, never what the route itself does when reached directly (a
 * bookmark, a shared link, an old tab). The API does refuse the request
 * (verifyTenant's nation gate, 404), but the page ignored that error and
 * rendered its ordinary empty state — 'No consultations.' reads as a real,
 * checked answer, not as 'this service does not run here'.
 */
import type { ReactNode } from 'react';
import { useBranch } from '../lib/branchContext';
import { moduleVisibleInNation, MODULE_NATIONS } from '../lib/moduleNations';

const NATION_LABEL: Record<string, string> = { ENG: 'England', WLS: 'Wales' };

export function DevolvedRoute({ labelKey, children }: { labelKey: keyof typeof MODULE_NATIONS; children: ReactNode }): JSX.Element {
  const { nation } = useBranch();
  if (moduleVisibleInNation(labelKey, nation)) return <>{children}</>;
  return (
    <div className="content">
      <div className="pg-stack pg-std">
        <div className="card" data-testid="devolved-not-available">
          <div className="ch"><span className="ct">Not available in {NATION_LABEL[nation] ?? nation}</span></div>
          <div className="cb" style={{ color: 'var(--muted)' }}>
            This service is commissioned for a different nation and is not offered to this branch.
          </div>
        </div>
      </div>
    </div>
  );
}
